/**
 * Run one tool over every row of a list (every guest, every session) and hand back a ZIP.
 *
 * The sibling of `runBatch` in `../batch`, which loops over files. Rows run one after another so memory
 * stays low and one bad row cannot stop the rest; a failure is recorded on that row only.
 */
import JSZip from "jszip";
import { uniqueZipNames, type BatchStatus } from "../batch";

export interface RowResult { blob: Blob; fileName: string }

export interface RowJob<T> {
  index: number;
  row: T;
  status: BatchStatus;
  result?: RowResult;
  error?: string;
}

export async function runRowBatch<T>(
  rows: readonly T[],
  process: (row: T, index: number) => Promise<RowResult>,
  onUpdate: (jobs: RowJob<T>[]) => void = () => {},
  shouldStop: () => boolean = () => false,
): Promise<RowJob<T>[]> {
  let state: RowJob<T>[] = rows.map((row, index) => ({ index, row, status: "queued" }));
  const push = (i: number, patch: Partial<RowJob<T>>) => { state = state.map((j, k) => (k === i ? { ...j, ...patch } : j)); onUpdate(state); };
  onUpdate(state);
  for (let i = 0; i < state.length; i++) {
    if (shouldStop()) { push(i, { status: "skipped" }); continue; }
    push(i, { status: "working" });
    try { push(i, { status: "done", result: await process(state[i].row, i) }); }
    catch (e) { push(i, { status: "failed", error: e instanceof Error ? e.message : "This row could not be processed." }); }
  }
  return state;
}

export function rowBatchTotals<T>(jobs: RowJob<T>[]) {
  return {
    done: jobs.filter((j) => j.status === "done").length,
    failed: jobs.filter((j) => j.status === "failed").length,
    skipped: jobs.filter((j) => j.status === "skipped").length,
  };
}

/** Packs finished rows into one ZIP, with a short report of any that failed. `describe` names a row in that report. */
export async function zipRowResults<T>(jobs: RowJob<T>[], describe: (row: T) => string = (r) => String(r)): Promise<Blob> {
  const done = jobs.filter((j) => j.status === "done" && j.result);
  const names = uniqueZipNames(done.map((j) => j.result!.fileName));
  const zip = new JSZip();
  // JSZip reads a Blob directly only in some browsers; an ArrayBuffer works everywhere (and in Node).
  const buffers = await Promise.all(done.map((j) => j.result!.blob.arrayBuffer()));
  done.forEach((_, i) => zip.file(names[i], buffers[i]));
  const failed = jobs.filter((j) => j.status === "failed");
  if (failed.length) zip.file("not-made.txt", failed.map((j) => `Row ${j.index + 1} (${describe(j.row)}): ${j.error ?? "failed"}`).join("\r\n") + "\r\n");
  return zip.generateAsync({ type: "blob" });
}
